import { Button } from '@strata/react-buttons';
import { RequestType, RequestTypeDisplayTextMap } from '@strata/react-chat';
import { FormHeaderAndTabbedContent } from '@strata/react-form-header-and-content';
import { PageTitle } from '@strata/react-page-title';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { SubmitNewRequestDialog } from 'components/requests/dialogs/SubmitNewRequestDialog';
import { RequestArchiveGrid } from 'components/requests/RequestArchiveGrid';
import { RequestsGrid } from 'components/requests/RequestsGrid';
import { PayerRequestWritePermissions, ReadPermissionRequestTypeMap } from 'services/constants';
import { useAppSelector } from 'store';
import { useOpenRequestCounts } from 'store/api/payerRequestApiThunks';

export const RequestsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [isSubmitNewRequestDialogOpen, setIsSubmitNewRequestDialogOpen] = useState(false);

    const { permissions } = useAppSelector((state) => state.auth);

    const requestTypes = useMemo(
        () => [
            ...Object.entries(ReadPermissionRequestTypeMap)
                // Only show requestTypes user has permissions for
                .filter(([permission]) => permissions.includes(permission))
                .map(([_, requestType]) => requestType),
            // If user has access to page they can see archive
            RequestType.ARCHIVE,
        ],
        [permissions],
    );

    const activeRequestType =
        (location.hash
            .replace(/^#/, '')
            .toLocaleUpperCase() as (typeof RequestType)[keyof typeof RequestType]) ??
        requestTypes[0];

    useEffect(() => {
        if (!requestTypes.includes(activeRequestType)) {
            navigate({
                ...location,
                hash: requestTypes[0]?.toLocaleLowerCase(),
            });
        }
    }, [activeRequestType, requestTypes, location, navigate]);

    const payerId = useAppSelector((state) => state.views.payerId);
    const { data: requestCounts } = useOpenRequestCounts();

    return (
        <FormHeaderAndTabbedContent
            rowOneContentHeader={
                <div
                    className="flex__gap--medium"
                    style={{
                        flexGrow: 1,
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                    }}
                >
                    <PageTitle title="Requests" />
                    <SubmitNewRequestDialog
                        isOpen={isSubmitNewRequestDialogOpen}
                        onClose={() => setIsSubmitNewRequestDialogOpen(false)}
                    />
                    <Button
                        label="Submit Request"
                        intent={Button.Intent.PRIMARY}
                        disabled={Object.values(PayerRequestWritePermissions).every(
                            (permission) => !permissions.includes(permission),
                        )}
                        onClick={() => setIsSubmitNewRequestDialogOpen(true)}
                    />
                </div>
            }
            tabs={[
                ...requestTypes.map((requestType) => ({
                    title: RequestTypeDisplayTextMap[requestType],
                    className: 'tabbed-grid-content',
                    badgeCount: requestCounts?.[payerId ?? '']?.[requestType],
                    mainContent:
                        requestType === RequestType.ARCHIVE ? (
                            <RequestArchiveGrid />
                        ) : (
                            <RequestsGrid requestType={requestType} />
                        ),
                })),
            ]}
            activeTab={RequestTypeDisplayTextMap[activeRequestType]}
            onTabSelect={(tab) =>
                navigate({
                    ...location,
                    hash: (
                        Object.keys(
                            RequestTypeDisplayTextMap,
                        ) as (typeof RequestType)[keyof typeof RequestType][]
                    )
                        .find((requestType) => RequestTypeDisplayTextMap[requestType] === tab)
                        ?.toLocaleLowerCase(),
                })
            }
        />
    );
};
